import i18n from 'i18next';

// The app's i18next instance (initialised by src/i18n at startup). Read directly, not through src/i18n, so a test
// that mocks react-i18next can still load this module; uninitialised, a message is its key, the tests' idiom.
const tr = (key: string, opts?: Record<string, unknown>): string => (i18n.isInitialized ? i18n.t(key, opts) : key);

/**
 * The fleet upload primitive's pure rules, ported from ptc-supervision
 * (`src/components/upload/uploadQueue.ts`, contract in its `docs/upload-primitive.md`). `extensionOf`,
 * `formatBytes`, `validateFile` and `suggestType` are verbatim, with i18n keys under `upload.`.
 * The statetax half below them is the rules every upload surface here applies, and the router for a
 * drop onto a one-document slot.
 *
 * Every rule list must EQUAL the server's. `uploadRules.serverGuard.test.ts` reads the C# and fails if
 * either side moves alone.
 */

export interface UploadType {
  value: string;
  label: string;
  /** Filename patterns that suggest this type (U2: see `suggestType` for how overlaps resolve). */
  match?: RegExp[];
}

export interface UploadRules {
  /** Lower-case extensions without the dot. They must equal the server's list (pinned by a test). */
  extensions: readonly string[];
  maxBytes: number;
}

export function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 && dot < name.length - 1 ? name.slice(dot + 1).toLowerCase() : '';
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** U3: refused up front, with the reason a person can act on. Null when the file may be sent. */
export function validateFile(file: File, rules: UploadRules): string | null {
  const ext = extensionOf(file.name);
  if (!ext || !rules.extensions.includes(ext)) {
    return tr('upload.unsupported', { ext: ext ? ` (.${ext})` : '', allowed: rules.extensions.join(', ') });
  }
  if (file.size === 0) return tr('upload.empty');
  if (file.size > rules.maxBytes) {
    return tr('upload.tooLarge', { size: formatBytes(file.size), limit: formatBytes(rules.maxBytes) });
  }
  return null;
}

/**
 * U2: the type a filename suggests, or null. When there's no suggestion the person chooses; nothing is guessed.
 *
 * - A match lying INSIDE a longer match of another type yields to it.
 * - Two SEPARATE matches of different types mean the name is ambiguous, so there is no suggestion
 *   rather than a confident wrong one.
 */
export function suggestType(fileName: string, types: readonly UploadType[]): string | null {
  const base = fileName.replace(/\.[^.]+$/, '').replace(/[_]+/g, ' ');
  const hits: { value: string; start: number; end: number }[] = [];
  for (const t of types) {
    for (const re of t.match ?? []) {
      const m = re.exec(base);
      if (m) hits.push({ value: t.value, start: m.index, end: m.index + m[0].length });
    }
  }
  const inside = (a: (typeof hits)[number], b: (typeof hits)[number]) =>
    a.value !== b.value && b.start <= a.start && a.end <= b.end && b.end - b.start > a.end - a.start;
  const kept = hits.filter((h) => !hits.some((o) => inside(h, o)));
  const values = new Set(kept.map((h) => h.value));
  return values.size === 1 ? [...values][0] : null;
}

// ── statetax ────────────────────────────────────────────────────────────────────────────────────────

/** Both upload endpoints cap a request at 25 MB (`MaxUploadBytes` in StateTaxClientFunctions and ComplianceTaskFunctions). */
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/**
 * A filed return or a payment confirmation (the Reconcile slots and the manual-filing dialog). PDF only: the
 * reader behind both extract endpoints is `PdfPigTextExtractor`, so an image would be stored and never read.
 */
export const FILING_DOCUMENT_RULES: UploadRules = { extensions: ['pdf'], maxBytes: MAX_UPLOAD_BYTES };

/** A source record (POS report, sales summary): every type `AttachmentContentType` stores and the source engine reads. */
export const SOURCE_RULES: UploadRules = {
  extensions: ['pdf', 'png', 'jpg', 'jpeg', 'webp', 'csv', 'xlsx'],
  maxBytes: MAX_UPLOAD_BYTES,
};

export const acceptFor = (rules: UploadRules): string => rules.extensions.map((e) => `.${e}`).join(',');

export type FilingDocumentKind = 'return' | 'payment-confirmation';

/**
 * U2 for the two filing documents. Names are split at camelCase and at - . so "2026.06 ReturnConfirmation.pdf"
 * reads "Return Confirmation", which is a RETURN: the longer match contains the payment pattern "confirmation".
 */
const FILING_DOCUMENT_TYPES: readonly UploadType[] = [
  { value: 'return', label: 'return', match: [/\breturns?( confirmation)?\b/i, /\bdr ?15[a-z]*\b/i] },
  {
    value: 'payment-confirmation',
    label: 'payment-confirmation',
    match: [/\bpayment( confirmation)?\b/i, /\bconfirmation\b/i, /\breceipt\b/i, /\bpaid\b/i, /\bach\b/i, /\bdebit\b/i],
  },
];

export function suggestFilingDocumentKind(
  fileName: string,
  offered: readonly FilingDocumentKind[],
): FilingDocumentKind | null {
  const words = fileName.replace(/\.[^.]+$/, '').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[-.]+/g, ' ');
  const kind = suggestType(words, FILING_DOCUMENT_TYPES.filter((t) => offered.includes(t.value as FilingDocumentKind)));
  return kind as FilingDocumentKind | null;
}

export interface RoutedDrop {
  /** The files to use, each on the slot it goes to. */
  placed: { kind: FilingDocumentKind; file: File }[];
  /** One sentence per refusal, naming the file and why; shown to the person, nothing of it is sent. */
  refused: string[];
}

/**
 * Where the files dropped on a one-document slot go.
 *
 * - **U3:** each file is validated first. A refused file is named with its reason and never sent.
 * - **One valid file** goes to the slot it was dropped on. The person chose the slot.
 * - **Several valid files** are each typed from the filename (U2). If every file names a different offered
 *   slot, each goes to its own; dropping the return and the payment confirmation together just works.
 * - Otherwise **nothing is placed** and every file is named (STX-UPLOAD-02). Keeping the first file would file
 *   whichever came first as the slot's document.
 */
export function routeDrop(
  files: readonly File[],
  droppedOn: FilingDocumentKind,
  offered: readonly FilingDocumentKind[],
  rules: UploadRules = FILING_DOCUMENT_RULES,
): RoutedDrop {
  const refused: string[] = [];
  const valid: File[] = [];
  for (const file of files) {
    const why = validateFile(file, rules);
    if (why) refused.push(tr('upload.refusedFile', { name: file.name, reason: why }));
    else valid.push(file);
  }
  if (valid.length === 0) return { placed: [], refused };
  if (valid.length === 1) return { placed: [{ kind: droppedOn, file: valid[0] }], refused };

  const kinds = valid.map((f) => suggestFilingDocumentKind(f.name, offered));
  const distinct = new Set(kinds);
  if (valid.length <= offered.length && !kinds.includes(null) && distinct.size === valid.length) {
    return { placed: valid.map((file, i) => ({ kind: kinds[i]!, file })), refused };
  }
  refused.push(tr('upload.cannotPlace', { count: valid.length, names: valid.map((f) => f.name).join(', ') }));
  return { placed: [], refused };
}
